import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Collapse, Modal, Progress, Space, Statistic, Typography, Row, Col, Spin,
} from 'antd';
import { CloudDownloadOutlined } from '@ant-design/icons';

const { Text, Paragraph } = Typography;

const ESTADOS = [
  {
    clave: 'programado_ahora',
    etiqueta: 'Programadas ahora',
    color: '#389e0d',
  },
  {
    clave: 'ya_programado',
    etiqueta: 'Ya estaban programadas',
    color: '#8c8c8c',
  },
  {
    // El más importante del desglose: es trabajo pendiente real, no ruido.
    // e.firmas que mifactura cree cargadas y SW no tiene.
    clave: 'sin_efirma_en_sw',
    etiqueta: 'Sin e.firma en SW',
    color: '#d46b08',
  },
  {
    clave: 'fallido',
    etiqueta: 'Fallidas',
    color: '#cf1322',
  },
];

/**
 * Programación masiva de descargas del SAT.
 *
 * TRES MOMENTOS: alcance → avance → desglose. Al abrirse NO programa nada; hay
 * que confirmar. Un proceso que empieza a escribir en cuanto se abre una
 * pantalla no se puede cancelar, y este toca la configuración de descargas de
 * todos los contribuyentes del alcance.
 *
 * RECORRE EN TANDAS del tamaño que impone el servidor. Programar un RFC cuesta
 * hasta tres llamadas a SW, así que el alcance entero en una sola petición
 * pasaría del tiempo de ejecución del servidor: no tardaría, se rompería a
 * medias. Aquí se ve el avance y se puede detener.
 *
 * Cada RFC es independiente e idempotente: detener o cerrar no deja nada a
 * medias, y volver a ejecutar retoma lo que falta.
 */
const ProgramacionDescargasModal = ({
  abierto,
  onCerrar,
  cargarAlcance,
  procesarTanda,
  titulo = 'Programar descargas del SAT',
}) => {
  const [alcance, setAlcance] = useState(null);
  const [cargandoAlcance, setCargandoAlcance] = useState(false);
  const [errorAlcance, setErrorAlcance] = useState('');

  const [corriendo, setCorriendo] = useState(false);
  // El bucle de tandas no ve el estado de React que cambia mientras corre, así
  // que Detener se comunica por referencia. El estado es solo para el botón.
  const [detener, setDetener] = useState(false);
  const detenerRef = React.useRef(false);
  const [procesados, setProcesados] = useState(0);
  const [enCurso, setEnCurso] = useState(null);
  const [resultados, setResultados] = useState([]);
  const [termino, setTermino] = useState(false);
  const [errorProceso, setErrorProceso] = useState('');

  const reiniciar = useCallback(() => {
    setCorriendo(false);
    setDetener(false);
    setProcesados(0);
    setEnCurso(null);
    setResultados([]);
    setTermino(false);
    setErrorProceso('');
  }, []);

  useEffect(() => {
    if (!abierto) return;

    reiniciar();
    setAlcance(null);
    setErrorAlcance('');
    setCargandoAlcance(true);

    cargarAlcance()
      .then(setAlcance)
      .catch((err) =>
        setErrorAlcance(err?.response?.data?.error || 'No se pudo consultar el alcance'))
      .finally(() => setCargandoAlcance(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abierto]);

  const ejecutar = async (empresas) => {
    if (!empresas || empresas.length === 0) return;

    const tope = Math.max(1, Number(alcance?.lote_maximo) || 1);

    setCorriendo(true);
    setDetener(false);
    setTermino(false);
    setErrorProceso('');
    setProcesados(0);
    setResultados([]);

    const acumulado = [];
    let detenido = false;

    detenerRef.current = false;

    for (let i = 0; i < empresas.length; i += tope) {
      if (detenerRef.current) {
        detenido = true;
        break;
      }

      const tanda = empresas.slice(i, i + tope);
      setEnCurso(tanda[0]);

      try {
        // eslint-disable-next-line no-await-in-loop
        const salida = await procesarTanda(tanda.map((e) => e.company_id));
        acumulado.push(...(salida?.resultados || []));
      } catch (err) {
        // Una tanda que falla no invalida lo ya programado: se anota y sigue.
        setErrorProceso(err?.response?.data?.error || 'Una tanda no se pudo procesar');
        tanda.forEach((e) => {
          acumulado.push({ ...e, estado: 'fallido', motivo: 'La tanda no se pudo procesar' });
        });
      }

      setResultados([...acumulado]);
      setProcesados(Math.min(i + tanda.length, empresas.length));
    }

    setEnCurso(null);
    setCorriendo(false);
    setTermino(true);
    if (detenido) {
      setErrorProceso('Proceso detenido. Lo ya programado sigue programado.');
    }
  };

  useEffect(() => {
    detenerRef.current = detener;
  }, [detener]);

  const total = alcance?.total || 0;
  const empresas = alcance?.empresas || [];

  const conteo = (clave) => resultados.filter((r) => r.estado === clave).length;

  const fallidos = resultados.filter((r) => r.estado === 'fallido');

  const paneles = ESTADOS.map((estado) => {
    const filas = resultados.filter((r) => r.estado === estado.clave);
    return {
      key: estado.clave,
      label: (
        <span>
          <Text style={{ color: estado.color }}>{estado.etiqueta}</Text>{' '}
          <Text strong>{filas.length}</Text>
        </span>
      ),
      children: filas.length === 0
        ? <Text type="secondary">Ninguna.</Text>
        : (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            {filas.map((f) => (
              <Text key={f.company_id} style={{ fontSize: 12 }}>
                <Text code>{f.tax_id || '—'}</Text> {f.razon_social}
                {f.motivo ? <Text type="secondary"> · {f.motivo}</Text> : null}
              </Text>
            ))}
          </Space>
        ),
    };
  });

  return (
    <Modal
      open={abierto}
      onCancel={corriendo ? undefined : onCerrar}
      maskClosable={!corriendo}
      closable={!corriendo}
      destroyOnHidden
      width={640}
      title={<span><CloudDownloadOutlined /> {titulo}</span>}
      footer={
        termino
          ? [
            fallidos.length > 0 ? (
              <Button
                key="reintentar"
                onClick={() => ejecutar(fallidos.map((f) => ({ company_id: f.company_id, tax_id: f.tax_id, razon_social: f.razon_social })))}
              >
                Reintentar las {fallidos.length} fallidas
              </Button>
            ) : null,
            <Button key="cerrar" type="primary" onClick={onCerrar}>Cerrar</Button>,
          ]
          : corriendo
            ? [
              <Button key="detener" danger onClick={() => setDetener(true)}>Detener</Button>,
            ]
            : [
              <Button key="cancelar" onClick={onCerrar}>Cancelar</Button>,
              <Button
                key="programar"
                type="primary"
                disabled={cargandoAlcance || total === 0}
                onClick={() => ejecutar(empresas)}
              >
                {total > 0 ? `Programar ${total}` : 'Programar'}
              </Button>,
            ]
      }
    >
      {cargandoAlcance && <Spin />}

      {!!errorAlcance && <Alert type="error" showIcon message={errorAlcance} />}

      {/* Momento 1: qué se va a hacer, antes de hacerlo. */}
      {!cargandoAlcance && !errorAlcance && !corriendo && !termino && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {total === 0 ? (
            <Alert
              type="info"
              showIcon
              message="No hay nada que programar"
              description="Ninguna empresa de este alcance tiene e.firma cargada, así que no hay descargas que SW pueda ejecutar."
            />
          ) : (
            <>
              <Row gutter={16}>
                <Col span={8}><Statistic title="Con e.firma" value={total} /></Col>
                <Col span={8}><Statistic title="Sin registro previo" value={alcance?.sin_registro_previo || 0} /></Col>
                <Col span={8}><Statistic title="Con registro previo" value={alcance?.con_registro_previo || 0} /></Col>
              </Row>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                SW descargará emitidos y recibidos cada día, 3 días hacia atrás. Cada RFC se
                verifica en SW antes de programarlo, así que ejecutarlo de nuevo no duplica nada.
              </Paragraph>
            </>
          )}
        </Space>
      )}

      {/* Momento 2: avance, ya clasificando. Una barra muda no dice nada. */}
      {corriendo && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Progress percent={total ? Math.round((procesados / total) * 100) : 0} />
          <Text>{procesados} / {total}</Text>
          {enCurso && (
            <Text type="secondary">
              Verificando <Text code>{enCurso.tax_id}</Text> · {enCurso.razon_social}
            </Text>
          )}
          <Row gutter={16}>
            {ESTADOS.map((e) => (
              <Col span={6} key={e.clave}>
                <Statistic
                  title={<span style={{ fontSize: 12 }}>{e.etiqueta}</span>}
                  value={conteo(e.clave)}
                  valueStyle={{ fontSize: 18, color: e.color }}
                />
              </Col>
            ))}
          </Row>
        </Space>
      )}

      {/* Momento 3: desglose accionable. "3 sin e.firma" sin nombres no sirve. */}
      {termino && (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {!!errorProceso && <Alert type="warning" showIcon message={errorProceso} />}
          <Text>Se procesaron {resultados.length} de {total}.</Text>
          <Collapse items={paneles} />
          {conteo('sin_efirma_en_sw') > 0 && (
            <Alert
              type="warning"
              showIcon
              message="Hay e.firmas que mifactura cree cargadas y SW no tiene"
              description="Esos RFC no pueden descargar nada hasta que su e.firma se vuelva a cargar. Es trabajo pendiente real, no un error del proceso."
            />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default ProgramacionDescargasModal;
